import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { HedgeAnalysis, HedgePosition, MarginData } from '@/lib/hedgeAnalysis';
import { CheckCircle, XCircle, Loader2, Brain, Trophy, AlertTriangle, Sparkles, BarChart3, ShoppingCart } from 'lucide-react';

interface ModelRecommendation {
  ticker: string;
  strategy: string;
  strategyName: string;
  score: number;
  rationale: string;
  estimatedCost: string;
  riskReduction: string;
  priority: string;
}

interface ModelResult {
  portfolioScore: number;
  overallRisk: string;
  marginAssessment: string;
  recommendations: ModelRecommendation[];
  portfolioStrategy: { name: string; description: string; steps: string[] };
  warnings: string[];
}

interface ModelResponse {
  id: string;
  label: string;
  status: 'pending' | 'fetching' | 'success' | 'error';
  error?: string | null;
  result?: ModelResult | null;
}

interface Props {
  models: ModelResponse[];
  loading: boolean;
  onTriggerOptionalModel?: (modelId: string) => void;
  optionalModels?: { id: string; label: string }[];
  analyses?: HedgeAnalysis[];
  marginData?: MarginData | null;
  positions?: HedgePosition[];
}

function scoreColor(score: number) {
  if (score >= 8) return 'text-green-600 dark:text-green-400 bg-green-500/10';
  if (score >= 5) return 'text-amber-600 dark:text-amber-400 bg-amber-500/10';
  return 'text-red-600 dark:text-red-400 bg-red-500/10';
}

function riskBadge(risk: string) {
  const r = risk?.toLowerCase();
  if (r === 'low') return 'bg-green-500/20 text-green-700 dark:text-green-400';
  if (r === 'medium') return 'bg-amber-500/20 text-amber-700 dark:text-amber-400';
  if (r === 'high') return 'bg-red-500/20 text-red-700 dark:text-red-400';
  return 'bg-red-600/20 text-red-800 dark:text-red-300';
}

function priorityColor(p: string) {
  if (p === 'high') return 'bg-red-500/20 text-red-700 dark:text-red-400';
  if (p === 'medium') return 'bg-amber-500/20 text-amber-700 dark:text-amber-400';
  return 'bg-green-500/20 text-green-700 dark:text-green-400';
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'pending') return <Loader2 className="h-4 w-4 text-muted-foreground" />;
  if (status === 'fetching') return <Loader2 className="h-4 w-4 text-primary animate-spin" />;
  if (status === 'success') return <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />;
  return <XCircle className="h-4 w-4 text-destructive" />;
}

// Animated progress for fetching models
function ModelProgressBar({ status }: { status: string }) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (status !== 'fetching') {
      setProgress(status === 'success' ? 100 : status === 'error' ? 100 : 0);
      return;
    }
    setProgress(5);
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev + 0.1;
        if (prev >= 70) return prev + 0.3;
        if (prev >= 40) return prev + 0.8;
        return prev + 2;
      });
    }, 500);
    return () => clearInterval(interval);
  }, [status]);

  if (status === 'pending') return null;

  return (
    <Progress
      value={Math.min(progress, status === 'success' || status === 'error' ? 100 : 95)}
      className={`h-1.5 mt-2 ${status === 'error' ? '[&>div]:bg-destructive' : status === 'success' ? '[&>div]:bg-green-500' : ''}`}
    />
  );
}

function computeConsensus(models: ModelResponse[]): {
  avgScore: number;
  topStrategies: { strategy: string; avgScore: number; count: number }[];
  agreementLevel: string;
} {
  const successful = models.filter(m => m.status === 'success' && m.result);
  if (successful.length === 0) return { avgScore: 0, topStrategies: [], agreementLevel: 'N/A' };

  const avgScore = successful.reduce((s, m) => s + (m.result!.portfolioScore || 0), 0) / successful.length;

  const strategyMap: Record<string, { scores: number[]; count: number }> = {};
  successful.forEach(m => {
    m.result!.recommendations?.forEach(r => {
      const key = r.strategy;
      if (!strategyMap[key]) strategyMap[key] = { scores: [], count: 0 };
      strategyMap[key].scores.push(r.score);
      strategyMap[key].count++;
    });
  });

  const topStrategies = Object.entries(strategyMap)
    .map(([strategy, data]) => ({
      strategy,
      avgScore: data.scores.reduce((a, b) => a + b, 0) / data.scores.length,
      count: data.count,
    }))
    .sort((a, b) => b.avgScore - a.avgScore)
    .slice(0, 5);

  const risks = successful.map(m => m.result!.overallRisk?.toLowerCase());
  const agreement = new Set(risks).size;
  const agreementLevel = agreement === 1 ? 'Full Consensus' : agreement <= 2 ? 'Mostly Aligned' : 'Divergent';

  return { avgScore, topStrategies, agreementLevel };
}

// Compute quality & consistency scores per model
function computeModelScores(models: ModelResponse[]) {
  const successful = models.filter(m => m.status === 'success' && m.result);
  if (successful.length === 0) return [];

  // Gather all recommendations across models for consistency comparison
  const allStrategies: Record<string, string[]> = {}; // ticker -> strategies recommended
  successful.forEach(m => {
    m.result!.recommendations?.forEach(r => {
      if (!allStrategies[r.ticker]) allStrategies[r.ticker] = [];
      allStrategies[r.ticker].push(r.strategy);
    });
  });

  return successful.map(m => {
    const r = m.result!;
    const recs = r.recommendations || [];

    // Quality: based on completeness and depth
    let quality = 0;
    if (r.portfolioScore > 0) quality += 15;
    if (r.overallRisk) quality += 10;
    if (r.marginAssessment && r.marginAssessment.length > 20) quality += 10;
    if (recs.length > 0) quality += Math.min(recs.length * 5, 25);
    if (recs.every(rec => rec.rationale && rec.rationale.length > 20)) quality += 15;
    if (recs.every(rec => rec.estimatedCost)) quality += 10;
    if (r.portfolioStrategy?.steps?.length > 0) quality += 10;
    if (r.warnings?.length > 0) quality += 5;
    quality = Math.min(quality, 100);

    // Consistency: how much this model agrees with others
    let consistencyMatches = 0;
    let consistencyTotal = 0;
    recs.forEach(rec => {
      const otherStrategies = successful
        .filter(om => om.id !== m.id)
        .flatMap(om => om.result!.recommendations?.filter(or => or.ticker === rec.ticker).map(or => or.strategy) || []);
      if (otherStrategies.length > 0) {
        consistencyTotal++;
        if (otherStrategies.includes(rec.strategy)) consistencyMatches++;
      }
    });
    const consistency = consistencyTotal > 0 ? Math.round((consistencyMatches / consistencyTotal) * 100) : successful.length === 1 ? 100 : 0;

    return {
      id: m.id,
      label: m.label,
      portfolioScore: r.portfolioScore,
      risk: r.overallRisk,
      recommendations: recs.length,
      quality,
      consistency,
      warnings: r.warnings?.length || 0,
    };
  });
}

// Build actionable options order book from hedge analyses + LLM recommendations
function buildOptionsOrderBook(analyses: HedgeAnalysis[], models: ModelResponse[]) {
  if (!analyses || analyses.length === 0) return [];

  const successful = models.filter(m => m.status === 'success' && m.result);
  
  // Gather LLM strategy recommendations per ticker
  const llmStrategies: Record<string, { strategy: string; priority: string; score: number }[]> = {};
  successful.forEach(m => {
    m.result!.recommendations?.forEach((r: any) => {
      if (!llmStrategies[r.ticker]) llmStrategies[r.ticker] = [];
      llmStrategies[r.ticker].push({ strategy: r.strategy, priority: r.priority, score: r.score });
    });
  });

  const orders: {
    ticker: string;
    action: string; // 'Buy Put' | 'Buy Put + Sell Call' | 'Sell Call'
    type: string;
    strikePrice: string;
    expiration: string;
    premium: string;
    totalCost: string;
    contracts: number;
    priority: string;
    consensusScore: number;
  }[] = [];

  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 90);
  const expiry90 = expiryDate.toISOString().split('T')[0];
  const expiry30 = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];

  analyses.forEach(a => {
    const ticker = a.position.ticker;
    const llmRecs = llmStrategies[ticker] || [];
    
    // Determine dominant strategy from LLMs
    const strategyVotes: Record<string, number> = {};
    llmRecs.forEach(r => {
      const key = r.strategy?.toLowerCase() || '';
      if (key.includes('put') && key.includes('call') || key.includes('collar')) strategyVotes['collar'] = (strategyVotes['collar'] || 0) + r.score;
      else if (key.includes('put') || key.includes('protective')) strategyVotes['put'] = (strategyVotes['put'] || 0) + r.score;
      else if (key.includes('call') || key.includes('covered')) strategyVotes['call'] = (strategyVotes['call'] || 0) + r.score;
      else strategyVotes['put'] = (strategyVotes['put'] || 0) + r.score; // default to protective put
    });

    const bestStrategy = Object.entries(strategyVotes).sort(([,a],[,b]) => b - a)[0]?.[0] || 'put';
    const avgScore = llmRecs.length > 0 ? llmRecs.reduce((s, r) => s + r.score, 0) / llmRecs.length : a.riskScore;
    const topPriority = llmRecs.sort((a, b) => b.score - a.score)[0]?.priority || (a.riskScore >= 7 ? 'high' : a.riskScore >= 4 ? 'medium' : 'low');

    // Best protective put (95% protection)
    const bestPut = a.protectivePuts?.[1] || a.protectivePuts?.[0];

    if (bestStrategy === 'collar' && a.collar) {
      orders.push({
        ticker,
        action: 'Buy Put + Sell Call',
        type: 'Collar',
        strikePrice: `Put $${a.collar.putStrike} / Call $${a.collar.callStrike}`,
        expiration: expiry90,
        premium: `Net $${Math.abs(a.collar.netPremium).toFixed(0)} ${a.collar.netPremium > 0 ? 'debit' : 'credit'}`,
        totalCost: `$${Math.abs(a.collar.netPremium).toFixed(0)}`,
        contracts: a.collar.contractsNeeded,
        priority: topPriority,
        consensusScore: avgScore,
      });
    } else if (bestStrategy === 'call' && a.coveredCall) {
      orders.push({
        ticker,
        action: 'Sell Call',
        type: 'Covered Call',
        strikePrice: `$${a.coveredCall.strikePrice}`,
        expiration: expiry30,
        premium: `$${a.coveredCall.estimatedPremium}/share`,
        totalCost: `+$${a.coveredCall.incomeGenerated.toFixed(0)} income`,
        contracts: a.coveredCall.contractsNeeded,
        priority: topPriority,
        consensusScore: avgScore,
      });
    } else if (bestPut) {
      orders.push({
        ticker,
        action: 'Buy Put',
        type: `Protective Put (${bestPut.protectionLevel})`,
        strikePrice: `$${bestPut.strikePrice}`,
        expiration: expiry90,
        premium: `$${bestPut.estimatedPremium}/share`,
        totalCost: `$${bestPut.totalCost.toFixed(0)}`,
        contracts: bestPut.contractsNeeded,
        priority: topPriority,
        consensusScore: avgScore,
      });
    }
  });

  return orders.sort((a, b) => b.consensusScore - a.consensusScore);
}

export function MultiLLMHedgeResults({ models, loading, onTriggerOptionalModel, optionalModels = [], analyses = [], marginData, positions = [] }: Props) {
  const consensus = computeConsensus(models);
  const modelScores = computeModelScores(models);
  const optionsOrders = buildOptionsOrderBook(analyses, models);

  return (
    <div className="space-y-6">
      {/* Model Status Panel with Progress Bars */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            Multi-Model Analysis Status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {models.map(m => (
              <div key={m.id} className={`p-3 rounded-lg border ${m.status === 'success' ? 'border-green-500/30 bg-green-500/5' : m.status === 'error' ? 'border-destructive/30 bg-destructive/5' : 'border-border bg-muted/30'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <StatusIcon status={m.status} />
                  <span className="text-sm font-medium text-foreground">{m.label}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {m.status === 'pending' && 'Waiting...'}
                  {m.status === 'fetching' && 'Analysing portfolio...'}
                  {m.status === 'success' && `Score: ${m.result?.portfolioScore}/10`}
                  {m.status === 'error' && (m.error?.slice(0, 60) || 'Failed')}
                </p>
                <ModelProgressBar status={m.status} />
              </div>
            ))}
          </div>
          {/* Optional model triggers */}
          {onTriggerOptionalModel && optionalModels.length > 0 && (() => {
            const pending = optionalModels.filter(om => !models.find(m => m.id === om.id));
            const fetching = optionalModels.filter(om => models.find(m => m.id === om.id)?.status === 'fetching');
            const anyRunnable = pending.length > 0;
            const anyFetching = fetching.length > 0;

            return (
              <div className="mt-3 pt-3 border-t border-border flex flex-wrap gap-2 items-start">
                {pending.length >= 2 && (
                  <Button variant="default" size="sm" onClick={() => pending.forEach(om => onTriggerOptionalModel(om.id))} className="gap-2">
                    <Sparkles className="h-4 w-4" />
                    Run All Optional Models
                  </Button>
                )}
                {optionalModels.map(om => {
                  const existing = models.find(m => m.id === om.id);
                  if (existing?.status === 'fetching') {
                    return (
                      <Button key={om.id} variant="outline" size="sm" disabled className="gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {om.label} analysing...
                      </Button>
                    );
                  }
                  if (existing) return null;
                  return (
                    <Button key={om.id} variant="outline" size="sm" onClick={() => onTriggerOptionalModel(om.id)} className="gap-2">
                      <Sparkles className="h-4 w-4" />
                      Run {om.label}
                    </Button>
                  );
                })}
                {(anyRunnable || anyFetching) && !anyFetching && (
                  <p className="text-xs text-muted-foreground w-full mt-1">Optional — these models take longer but provide deeper reasoning</p>
                )}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Model Quality & Consistency Scorecard */}
      {modelScores.length > 0 && (
        <Card className="border-primary/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Model Quality & Consistency Scorecard
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="font-semibold">Model</TableHead>
                    <TableHead className="text-center font-semibold">Portfolio Score</TableHead>
                    <TableHead className="text-center font-semibold">Risk Level</TableHead>
                    <TableHead className="text-center font-semibold">Recommendations</TableHead>
                    <TableHead className="text-center font-semibold">Quality</TableHead>
                    <TableHead className="text-center font-semibold">Consistency</TableHead>
                    <TableHead className="text-center font-semibold">Warnings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {modelScores.map(ms => (
                    <TableRow key={ms.id}>
                      <TableCell className="font-medium text-foreground">{ms.label}</TableCell>
                      <TableCell className="text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${scoreColor(ms.portfolioScore)}`}>
                          {ms.portfolioScore}/10
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className={riskBadge(ms.risk)}>{ms.risk}</Badge>
                      </TableCell>
                      <TableCell className="text-center text-muted-foreground">{ms.recommendations}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <Progress value={ms.quality} className="h-2 w-16" />
                          <span className={`text-xs font-bold ${ms.quality >= 70 ? 'text-green-600 dark:text-green-400' : ms.quality >= 40 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                            {ms.quality}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center gap-2 justify-center">
                          <Progress value={ms.consistency} className="h-2 w-16" />
                          <span className={`text-xs font-bold ${ms.consistency >= 70 ? 'text-green-600 dark:text-green-400' : ms.consistency >= 40 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'}`}>
                            {ms.consistency}%
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {ms.warnings > 0 ? (
                          <Badge variant="outline" className="bg-amber-500/20 text-amber-700 dark:text-amber-400">{ms.warnings}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Consensus Report */}
      {!loading && models.some(m => m.status === 'success') && (
        <Card className="border-primary/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              Quality Report — Consensus Analysis
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="p-3 bg-muted/40 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Avg Portfolio Score</p>
                <p className={`text-2xl font-bold ${scoreColor(consensus.avgScore)}`}>
                  {consensus.avgScore.toFixed(1)}/10
                </p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Models Succeeded</p>
                <p className="text-2xl font-bold text-foreground">
                  {models.filter(m => m.status === 'success').length}/{models.length}
                </p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Agreement</p>
                <p className="text-lg font-bold text-foreground">{consensus.agreementLevel}</p>
              </div>
            </div>

            {consensus.topStrategies.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">Top Strategies (across all models)</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Strategy</th>
                        <th className="text-center py-2 px-3 font-medium text-muted-foreground">Avg Score</th>
                        <th className="text-center py-2 px-3 font-medium text-muted-foreground">Mentions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consensus.topStrategies.map((s, i) => (
                        <tr key={i} className="border-b border-border/30">
                          <td className="py-2 px-3 font-medium text-foreground capitalize">{s.strategy.replace(/_/g, ' ')}</td>
                          <td className="py-2 px-3 text-center">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${scoreColor(s.avgScore)}`}>
                              {s.avgScore.toFixed(1)}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-center text-muted-foreground">{s.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Actionable Options Order Book */}
            {optionsOrders.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-primary" />
                  Recommended Options Order Book
                </h4>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="font-semibold">Ticker</TableHead>
                        <TableHead className="font-semibold">Action</TableHead>
                        <TableHead className="font-semibold">Type</TableHead>
                        <TableHead className="text-right font-semibold">Strike</TableHead>
                        <TableHead className="text-center font-semibold">Expiration</TableHead>
                        <TableHead className="text-right font-semibold">Premium</TableHead>
                        <TableHead className="text-right font-semibold">Total Cost</TableHead>
                        <TableHead className="text-center font-semibold">Contracts</TableHead>
                        <TableHead className="text-center font-semibold">Priority</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {optionsOrders.map((o, i) => (
                        <TableRow key={i} className="hover:bg-muted/30">
                          <TableCell className="font-mono font-bold text-foreground">{o.ticker}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={
                              o.action.includes('Buy Put') && o.action.includes('Sell')
                                ? 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30'
                                : o.action.includes('Buy')
                                ? 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30'
                                : 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30'
                            }>
                              {o.action}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{o.type}</TableCell>
                          <TableCell className="text-right font-mono text-foreground">{o.strikePrice}</TableCell>
                          <TableCell className="text-center text-sm text-muted-foreground">{o.expiration}</TableCell>
                          <TableCell className="text-right font-mono text-foreground">{o.premium}</TableCell>
                          <TableCell className="text-right font-mono font-medium text-foreground">{o.totalCost}</TableCell>
                          <TableCell className="text-center text-foreground">{o.contracts}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className={priorityColor(o.priority)}>{o.priority}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <p className="text-xs text-muted-foreground mt-2 italic">
                  * Strike prices & premiums are estimated. Verify with your broker before placing orders. Expiration dates are approximate (90d for puts/collars, 30d for covered calls).
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Margin Assessment & Reduction Strategy */}
      {!loading && marginData && positions.length > 0 && (
        <Card className="border-amber-500/20">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Margin Assessment & Reduction Strategy
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Margin Ratio Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-muted/40 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Margin Used (M275)</p>
                <p className="text-lg font-bold text-foreground">CHF {Math.round(marginData.marginRaw).toLocaleString()}</p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Portfolio Value (M163)</p>
                <p className="text-lg font-bold text-foreground">CHF {Math.round(marginData.portfolioValue).toLocaleString()}</p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Margin Ratio</p>
                <p className={`text-xl font-bold ${marginData.marginRatioPct > 50 ? 'text-destructive' : marginData.marginRatioPct > 30 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                  {marginData.marginRatioPct.toFixed(2)}%
                </p>
              </div>
              <div className="p-3 bg-muted/40 rounded-lg text-center">
                <p className="text-xs text-muted-foreground">Risk Level</p>
                <Badge variant="outline" className={
                  marginData.marginRatioPct > 50 ? 'bg-destructive/20 text-destructive' :
                  marginData.marginRatioPct > 30 ? 'bg-amber-500/20 text-amber-700 dark:text-amber-400' :
                  'bg-green-500/20 text-green-700 dark:text-green-400'
                }>
                  {marginData.marginRatioPct > 50 ? 'Critical' : marginData.marginRatioPct > 30 ? 'Elevated' : 'Acceptable'}
                </Badge>
              </div>
            </div>

            {/* BR Performance Reduction Strategy Table */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-2">
                Margin Reduction Strategy by Stock (BR Column Performance)
              </h4>
              <p className="text-xs text-muted-foreground mb-3">
                Sorted by BR performance — worst performers first. Reducing or closing positions with negative BR performance will most effectively lower your margin usage.
              </p>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">Ticker</TableHead>
                      <TableHead className="font-semibold">Stock Name</TableHead>
                      <TableHead className="text-right font-semibold">BR Perf %</TableHead>
                      <TableHead className="text-right font-semibold">Gain %</TableHead>
                      <TableHead className="text-right font-semibold">Value</TableHead>
                      <TableHead className="text-center font-semibold">Qty</TableHead>
                      <TableHead className="text-center font-semibold">Action</TableHead>
                      <TableHead className="font-semibold">Recommendation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {[...positions]
                      .sort((a, b) => (a.brPerformance || 0) - (b.brPerformance || 0))
                      .map((p, i) => {
                        const br = p.brPerformance || 0;
                        const gain = p.gainPct * 100;
                        const isNegBR = br < 0;
                        const isLowBR = br >= 0 && br < 5;
                        const isHighBR = br >= 15;
                        
                        let action = '';
                        let recommendation = '';
                        let actionColor = '';
                        
                        if (isNegBR && gain < 0) {
                          action = 'CLOSE';
                          recommendation = `Loss-making with negative BR (${br.toFixed(1)}%). Close to free margin immediately. Estimated margin freed: ~${Math.abs(br * marginData.marginRatio * 100).toFixed(1)}% of current margin.`;
                          actionColor = 'bg-destructive/15 text-destructive border-destructive/30';
                        } else if (isNegBR && gain >= 0) {
                          action = 'REDUCE';
                          recommendation = `Profitable but negative BR drag (${br.toFixed(1)}%). Sell 50% to lock gains and reduce margin. Consider protective put on remainder.`;
                          actionColor = 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30';
                        } else if (isLowBR && gain < 5) {
                          action = 'REVIEW';
                          recommendation = `Low BR performance (${br.toFixed(1)}%) with minimal gain. Monitor closely — candidate for reduction if margin pressure increases.`;
                          actionColor = 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30';
                        } else if (isLowBR && gain >= 5) {
                          action = 'HOLD';
                          recommendation = `Decent gains despite low BR (${br.toFixed(1)}%). Consider partial profit-taking to free margin while retaining upside exposure.`;
                          actionColor = 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30';
                        } else if (isHighBR) {
                          action = 'KEEP';
                          recommendation = `Strong BR performer (${br.toFixed(1)}%). Core position — contributes positively to portfolio. Protect with collar if concentrated.`;
                          actionColor = 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30';
                        } else {
                          action = 'MONITOR';
                          recommendation = `Moderate BR performance (${br.toFixed(1)}%). Stable position — no immediate action needed unless margin exceeds 40%.`;
                          actionColor = 'bg-muted text-muted-foreground border-border';
                        }

                        return (
                          <TableRow key={i} className={isNegBR ? 'bg-destructive/5' : ''}>
                            <TableCell className="font-mono font-bold text-foreground">{p.ticker}</TableCell>
                            <TableCell className="text-sm text-muted-foreground truncate max-w-[180px]">{p.stockName}</TableCell>
                            <TableCell className={`text-right font-mono font-bold ${br < 0 ? 'text-destructive' : br < 5 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                              {br.toFixed(2)}%
                            </TableCell>
                            <TableCell className={`text-right font-mono ${gain < 0 ? 'text-destructive' : 'text-green-600 dark:text-green-400'}`}>
                              {gain.toFixed(1)}%
                            </TableCell>
                            <TableCell className="text-right font-mono text-muted-foreground">
                              ${Math.round(p.totalValue).toLocaleString()}
                            </TableCell>
                            <TableCell className="text-center text-muted-foreground">{p.quantity}</TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={actionColor}>{action}</Badge>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground max-w-[300px]">{recommendation}</TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Summary */}
            {(() => {
              const closePositions = positions.filter(p => (p.brPerformance || 0) < 0 && p.gainPct < 0);
              const reducePositions = positions.filter(p => (p.brPerformance || 0) < 0 && p.gainPct >= 0);
              const totalCloseValue = closePositions.reduce((s, p) => s + p.totalValue, 0);
              const totalReduceValue = reducePositions.reduce((s, p) => s + p.totalValue * 0.5, 0);
              const estimatedFreed = totalCloseValue + totalReduceValue;
              const newMarginRatio = marginData.portfolioValue > 0
                ? ((marginData.marginRaw - estimatedFreed * 0.3) / marginData.portfolioValue) * 100
                : marginData.marginRatioPct;

              return (
                <div className="p-4 bg-muted/30 rounded-lg border border-border space-y-2">
                  <h4 className="text-sm font-semibold text-foreground">Summary</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Positions to Close</p>
                      <p className="font-bold text-destructive">{closePositions.length} (${Math.round(totalCloseValue).toLocaleString()})</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Positions to Reduce</p>
                      <p className="font-bold text-amber-600 dark:text-amber-400">{reducePositions.length} (${Math.round(totalReduceValue).toLocaleString()})</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Est. Capital Freed</p>
                      <p className="font-bold text-foreground">${Math.round(estimatedFreed).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Projected Margin Ratio</p>
                      <p className={`font-bold ${newMarginRatio > 30 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
                        {newMarginRatio.toFixed(2)}% (from {marginData.marginRatioPct.toFixed(2)}%)
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>
      )}

      {/* Per-Model Details */}
      {!loading && models.some(m => m.status === 'success') && (
        <Tabs defaultValue={models.find(m => m.status === 'success')?.id || models[0].id}>
          <TabsList className={`grid w-full ${models.length <= 2 ? 'grid-cols-2' : models.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
            {models.map(m => (
              <TabsTrigger key={m.id} value={m.id} disabled={m.status !== 'success'} className="text-xs md:text-sm gap-1">
                <StatusIcon status={m.status} />
                {m.label.split(' ')[0]}
              </TabsTrigger>
            ))}
          </TabsList>

          {models.filter(m => m.status === 'success' && m.result).map(m => (
            <TabsContent key={m.id} value={m.id} className="mt-4 space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="outline" className={riskBadge(m.result!.overallRisk)}>
                  Risk: {m.result!.overallRisk}
                </Badge>
                <Badge variant="outline" className={scoreColor(m.result!.portfolioScore)}>
                  Score: {m.result!.portfolioScore}/10
                </Badge>
              </div>

              <Card className="bg-muted/20">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-foreground mb-1">Margin Assessment</p>
                  <p className="text-sm text-muted-foreground">{m.result!.marginAssessment}</p>
                </CardContent>
              </Card>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 px-3 font-semibold text-foreground">Ticker</th>
                      <th className="text-left py-2 px-3 font-semibold text-foreground">Strategy</th>
                      <th className="text-center py-2 px-3 font-semibold text-foreground">Score</th>
                      <th className="text-center py-2 px-3 font-semibold text-foreground">Priority</th>
                      <th className="text-left py-2 px-3 font-semibold text-foreground">Cost</th>
                      <th className="text-left py-2 px-3 font-semibold text-foreground">Risk Reduction</th>
                    </tr>
                  </thead>
                  <tbody>
                    {m.result!.recommendations?.sort((a, b) => b.score - a.score).map((r, i) => (
                      <tr key={i} className="border-b border-border/30 hover:bg-muted/30">
                        <td className="py-2 px-3 font-mono font-medium text-foreground">{r.ticker}</td>
                        <td className="py-2 px-3 text-foreground">{r.strategyName}</td>
                        <td className="py-2 px-3 text-center">
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${scoreColor(r.score)}`}>
                            {r.score}/10
                          </span>
                        </td>
                        <td className="py-2 px-3 text-center">
                          <Badge variant="outline" className={priorityColor(r.priority)}>{r.priority}</Badge>
                        </td>
                        <td className="py-2 px-3 text-muted-foreground">{r.estimatedCost}</td>
                        <td className="py-2 px-3 text-muted-foreground">{r.riskReduction}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-2">
                {m.result!.recommendations?.map((r, i) => (
                  <div key={i} className="p-3 bg-muted/20 rounded-lg">
                    <p className="text-sm font-medium text-foreground">{r.ticker} — {r.strategyName}</p>
                    <p className="text-sm text-muted-foreground">{r.rationale}</p>
                  </div>
                ))}
              </div>

              {m.result!.portfolioStrategy && (
                <Card className="border-primary/20">
                  <CardContent className="p-4">
                    <p className="text-sm font-semibold text-primary mb-1">{m.result!.portfolioStrategy.name}</p>
                    <p className="text-sm text-muted-foreground mb-2">{m.result!.portfolioStrategy.description}</p>
                    <ol className="list-decimal list-inside space-y-1">
                      {m.result!.portfolioStrategy.steps?.map((step, i) => (
                        <li key={i} className="text-sm text-muted-foreground">{step}</li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>
              )}

              {m.result!.warnings?.length > 0 && (
                <Card className="border-amber-500/30 bg-amber-500/5">
                  <CardContent className="p-4">
                    <p className="text-sm font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4 w-4" /> Warnings
                    </p>
                    <ul className="space-y-1">
                      {m.result!.warnings.map((w, i) => (
                        <li key={i} className="text-sm text-muted-foreground">• {w}</li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}
